import {
  ABILITY_RULES,
  ACTIVE_COOLDOWN_TICKS,
  BUILDINGS,
  CATALOGUE,
  CELL_SIZE,
  DIFFICULTIES,
  FIXED_TICK_HZ,
  GRID_SIZE,
  LEAK_LIFE_DAMAGE,
  LOGICAL_BOARD_SIZE,
  MAPS,
  PROJECTILE_HIT_RADIUS,
  SCORE_PER_REWARD,
  SELL_FRACTION,
  STRUCTURE_FOOTPRINT_CELLS,
  TOWER_FIRE_ALIGNMENT_RADIANS,
  TOWERS,
  WAVE_SCRIPTS,
  enemyDefinition,
  isTowerId,
  structureLabel,
} from '../content/data';
import type { AbilityId, BuildingId, CatalogueId, DifficultyId, MapDefinition, MapId, TowerId, WaveDefinition, WaveGroup } from '../content/types';
import { BUILDING_UPGRADES, TOWER_UPGRADES, resolveTowerDefinition } from '../content/upgrades';
import type { BuildingUpgradeId, TowerUpgradeId } from '../content/upgrades';
import { angleTo, approachAngle, distanceSquared, moveTowards, wrapAngle } from './math';
import type { BarbedWireState, BuildingState, DeployedStructureSelection, EnemyState, MineState, ProjectileState, RunMode, RunState, SimEvent, SimulationSnapshot, TowerState, UnlockProgress, Vec2, VisualEvent, WallState } from './types';
import { resolveWave, scaleEnemyHealth, waveMetadata } from './waveResolver';

const MAX_EVENTS = 12;
const MAX_VISUAL_EVENTS = 64;
const TURN_CURVE_SAMPLES = 12;
const TICK_SECONDS = 1 / FIXED_TICK_HZ;
const DEFAULT_CONFUSE_PERCENT = TOWERS['cd1.tower.oil'].confuse!.percent;

const cloneVec = (value: Vec2): Vec2 => ({ x: value.x, y: value.y });
const cellKey = (x: number, y: number) => `${x},${y}`;
const abilityIds: readonly AbilityId[] = ['missile', 'wall', 'stop-time', 'mine', 'barbed-wire'];

/**
 * Rules-only fixed-tick simulation. Phaser and DOM code only read snapshots and dispatch actions here.
 * Deterministic ordering per tick: spawn -> movement -> targeting/fire -> projectile impact -> rewards/leaks -> wave result.
 */
export class Simulation {
  private state: RunState;
  private nextEntityId = 1;
  private nextVisualEventId = 1;
  private randomState: number;
  private wireSlowThisTick = new Map<number, number>();

  constructor(mapId: MapId, difficultyId: DifficultyId, seedOrRunMode: number | RunMode = 0xced10001, runMode: RunMode = 'fixed') {
    const seed = typeof seedOrRunMode === 'number' ? seedOrRunMode : 0xced10001;
    const selectedRunMode = typeof seedOrRunMode === 'string' ? seedOrRunMode : runMode;
    this.randomState = seed >>> 0;
    this.state = {
      tick: 0,
      seed,
      mapId,
      difficultyId,
      runMode: selectedRunMode,
      phase: 'prewave',
      paused: false,
      cash: DIFFICULTIES[difficultyId].startingCash.value,
      lives: DIFFICULTIES[difficultyId].startingLives.value,
      score: 0,
      completedWaves: 0,
      nextWaveIndex: 0,
      nextWaveCountdownTicks: DIFFICULTIES[difficultyId].waveIntervalTicks.value,
      activeWave: null,
      enemies: [],
      towers: [],
      buildings: [],
      walls: [],
      mines: [],
      barbedWires: [],
      projectiles: [],
      counters: {
        builtMissiles: 0, builtAA: 0, builtTotal: 0, destroyedAir: 0, kills: 0, earned: 0,
        builtHowitzers: 0, builtGauss: 0, builtOil: 0, builtNulificators: 0, wallsBuilt: 0,
        aoeKills: 0, teslaShots: 0, hammerShots: 0, thermonuclearBuilt: 0,
      },
      upgradeQuestFacts: { missileSurvivorIds: [], slowedEnemyIds: [] },
      events: [],
      visualEvents: [],
      unlocked: Object.fromEntries(CATALOGUE.map((entry) => [entry.id, entry.id === 'cd1.tower.missile' || entry.id === 'cd1.tower.aa'])),
      buildingUpgradeUnlocked: Object.fromEntries(
        Object.keys(BUILDING_UPGRADES).map((id) => [id, false]),
      ) as Record<BuildingUpgradeId, boolean>,
      abilityCooldowns: { missile: 0, wall: 0, 'stop-time': 0, mine: 0, 'barbed-wire': 0 },
      missileActions: 0,
      lastNotice: 'Choose a defense, then place it on solid canyon ground.',
    };
  }

  getSnapshot(): SimulationSnapshot {
    const { state } = this;
    const nextWave = this.resolveWave(state.nextWaveIndex + 1);
    return {
      tick: state.tick,
      mapId: state.mapId,
      difficultyId: state.difficultyId,
      runMode: state.runMode,
      phase: state.phase,
      paused: state.paused,
      cash: state.cash,
      lives: state.lives,
      score: state.score,
      completedWaves: state.completedWaves,
      nextWaveIndex: state.nextWaveIndex,
      nextWave: nextWave ? waveMetadata(nextWave) : null,
      nextWaveCountdownTicks: state.nextWaveCountdownTicks,
      activeWave: state.activeWave ? { ...state.activeWave } : null,
      enemyCount: state.enemies.length,
      towerCount: state.towers.length,
      buildingCount: state.buildings.length,
      wallCount: state.walls.length,
      mineCount: state.mines.length,
      barbedWireCount: state.barbedWires.length,
      projectileCount: state.projectiles.length,
      counters: { ...state.counters },
      unlocked: { ...state.unlocked },
      progress: this.getUnlockProgress().map((progress) => ({ ...progress })),
      buildingUpgradeProgress: this.getBuildingUpgradeProgress().map((progress) => ({ ...progress })),
      buildingUpgradeUnlocked: { ...state.buildingUpgradeUnlocked },
      availableTowerUpgrades: Object.fromEntries(
        Object.keys(TOWER_UPGRADES).map((id) => [id, this.isTowerUpgradeAvailable(id as TowerUpgradeId)]),
      ) as Record<TowerUpgradeId, boolean>,
      abilityCooldowns: { ...state.abilityCooldowns },
      visualEvents: state.visualEvents.map((event) => ({
        ...event,
        position: cloneVec(event.position),
        ...(event.target ? { target: cloneVec(event.target) } : {}),
      })),
      lastNotice: state.lastNotice,
    };
  }

  getState(): Readonly<RunState> { return this.state; }

  getTickSeconds(): number { return TICK_SECONDS; }

  /** Test-only fixture setup used by the browser QA hook; never reachable from game input. */
  debugPrimeVisualQa(): void {
    this.state.cash = 10_000;
    Object.keys(this.state.unlocked).forEach((id) => { this.state.unlocked[id] = true; });
    this.notice('notice', 'QA fixture armed.');
  }

  /** Test-only unlock transition used by browser presentation QA. */
  debugUnlockForQa(definitionId: CatalogueId): boolean {
    if (this.state.unlocked[definitionId]) return false;
    this.state.unlocked[definitionId] = true;
    return true;
  }

  /** Test-only quest unlock used to construct deterministic provider fixtures. */
  debugUnlockBuildingUpgradeForQa(upgradeId: BuildingUpgradeId): boolean {
    if (this.state.buildingUpgradeUnlocked[upgradeId]) return false;
    this.state.buildingUpgradeUnlocked[upgradeId] = true;
    return true;
  }

  isUnlocked(definitionId: CatalogueId): boolean { return this.state.unlocked[definitionId] === true; }

  abilityReady(ability: AbilityId): boolean {
    return this.abilitySourceCount(ability) > 0 && this.state.abilityCooldowns[ability] <= 0;
  }

  getAbilitySourceCount(ability: AbilityId): number { return this.abilitySourceCount(ability); }

  getMissileDamage(): number {
    const thermonuclearSilos = this.countBuildingUpgrades('silo.thermonuclear');
    return thermonuclearSilos === 0 ? ABILITY_RULES.missile.damage.value : 90 + (thermonuclearSilos - 1) * 30;
  }

  getEffectiveTowerRange(definitionId: TowerId, upgradeId?: TowerUpgradeId): number {
    const rangeBonusPercent = this.state.buildings.reduce((bonus, building) => {
      if (building.definitionId !== 'cd1.building.radar') return bonus;
      return bonus + (building.upgradeId === 'radar.orbital-uplink' ? 15 : 10);
    }, 0);
    return resolveTowerDefinition(definitionId, upgradeId).range.value * (100 + rangeBonusPercent) / 100;
  }

  getBuildingUpgradeProgress(): readonly UnlockProgress[] {
    const { counters, upgradeQuestFacts, buildingUpgradeUnlocked } = this.state;
    const values: Record<BuildingUpgradeId, { current: number; target: number }> = {
      'silo.thermonuclear': { current: upgradeQuestFacts.missileSurvivorIds.length, target: 3 },
      'silo.armament': { current: counters.builtHowitzers, target: 2 },
      'wall.mine-factory': { current: counters.aoeKills, target: 50 },
      'wall.wire-factory': { current: counters.wallsBuilt, target: 15 },
      'recycler.chemical-depot': { current: upgradeQuestFacts.slowedEnemyIds.length, target: 100 },
      'recycler.power-plant': { current: counters.teslaShots, target: 100 },
      'radar.tactical-atc': { current: counters.builtGauss, target: 5 },
      'radar.orbital-uplink': { current: counters.thermonuclearBuilt, target: 1 },
      'laboratory.particle-accelerator': { current: counters.hammerShots, target: 1 },
      'laboratory.black-site': { current: Math.min(counters.builtOil, counters.builtNulificators), target: 2 },
    };
    return Object.keys(BUILDING_UPGRADES).map((id) => {
      const upgradeId = id as BuildingUpgradeId;
      const unlocked = buildingUpgradeUnlocked[upgradeId];
      return { id: upgradeId, ...values[upgradeId], unlocked, active: !unlocked };
    });
  }

  isBuildingUpgradeUnlocked(upgradeId: BuildingUpgradeId): boolean {
    return this.state.buildingUpgradeUnlocked[upgradeId] === true;
  }

  isTowerUpgradeAvailable(upgradeId: TowerUpgradeId): boolean {
    return this.state.buildings.some((building) => {
      const buildingUpgrade = building.upgradeId ? BUILDING_UPGRADES[building.upgradeId] : undefined;
      return buildingUpgrade?.unlocks.includes(upgradeId) === true;
    });
  }

  getTowerUpgradeRequirement(upgradeId: TowerUpgradeId): string {
    const providers = Object.values(BUILDING_UPGRADES)
      .filter((upgrade) => upgrade.unlocks.includes(upgradeId))
      .map((upgrade) => upgrade.name);
    return providers.length > 0 ? `Build ${providers.join(' or ')}.` : 'No building currently provides this upgrade.';
  }

  getUnlockProgress(): readonly UnlockProgress[] {
    const state = this.state;
    const values: Record<string, { current: number; target: number }> = {
      'cd1.tower.howitzer': { current: state.missileActions, target: 1 },
      'cd1.tower.tesla': { current: this.countTowers('cd1.tower.goo'), target: 5 },
      'cd1.tower.gatling': { current: state.counters.destroyedAir, target: 40 },
      'cd1.tower.nulificator': { current: this.countBuildings('cd1.building.laboratory'), target: 1 },
      'cd1.tower.goo': { current: state.counters.destroyedAir, target: 5 },
      'cd1.tower.chain': { current: state.cash, target: 1000 },
      'cd1.tower.gauss': { current: this.countBuildings('cd1.building.radar'), target: 1 },
      'cd1.tower.oil': { current: state.walls.length, target: 10 },
      'cd1.building.silo': { current: this.countTowers('cd1.tower.missile'), target: 5 },
      'cd1.building.wall-factory': { current: this.countTowers() + this.countBuildings(), target: 15 },
      'cd1.building.recycler': { current: this.countTowers('cd1.tower.tesla') + this.countTowers('cd1.tower.chain'), target: 3 },
      'cd1.building.radar': { current: this.countTowers('cd1.tower.aa'), target: 7 },
      'cd1.building.laboratory': { current: this.countBuildings('cd1.building.wall-factory'), target: 2 },
    };
    const chains: readonly (readonly string[])[] = [
      ['cd1.tower.howitzer', 'cd1.tower.tesla', 'cd1.tower.gatling', 'cd1.tower.nulificator'],
      ['cd1.tower.goo', 'cd1.tower.chain', 'cd1.tower.gauss', 'cd1.tower.oil'],
      ['cd1.building.silo', 'cd1.building.wall-factory', 'cd1.building.recycler', 'cd1.building.radar', 'cd1.building.laboratory'],
    ];
    return chains.flatMap((chain) => chain.map((id, index) => {
      const unlocked = state.unlocked[id] === true;
      const predecessorUnlocked = index === 0 || state.unlocked[chain[index - 1]] === true;
      return { id, ...values[id], unlocked, active: !unlocked && predecessorUnlocked };
    }));
  }

  startNextWave(): boolean {
    if (this.state.paused) return false;
    if (!this.resolveWave(this.state.nextWaveIndex + 1)) return false;
    const skippedCountdown = Math.max(0, this.state.nextWaveCountdownTicks);
    if (this.state.phase === 'active') {
      this.creditSkippedCooldowns(skippedCountdown);
      return this.beginNextWave(true);
    }
    if (this.state.phase !== 'prewave') return false;
    this.creditSkippedCooldowns(skippedCountdown);
    return this.beginNextWave(true);
  }

  private beginNextWave(earlyRelease: boolean): boolean {
    const resolved = this.resolveWave(this.state.nextWaveIndex + 1);
    if (!resolved || (this.state.phase !== 'prewave' && this.state.phase !== 'active')) return false;
    const index = this.state.nextWaveIndex;
    const selected = resolved.definition;
    this.state.nextWaveIndex += 1;
    this.state.phase = 'active';
    this.state.activeWave = {
      index,
      ...waveMetadata(resolved),
      spawned: 0,
      total: selected.groups.reduce((total, group) => total + group.count, 0),
      nextSpawnTick: this.state.tick,
      groupIndex: 0,
      groupSpawned: 0,
    };
    this.state.nextWaveCountdownTicks = this.resolveWave(this.state.nextWaveIndex + 1) ? DIFFICULTIES[this.state.difficultyId].waveIntervalTicks.value : 0;
    // Program rows create their complete group together. Spawn now so a second PLAY release cannot replace an
    // unspawned active row, and so early releases genuinely overlap the units already in the field.
    this.spawnDueEnemies();
    const cycleNotice = resolved.completedCycles > 0 && resolved.baseWave === 1
      ? ` Cycle ${resolved.cycle} begins: enemy HP ${resolved.hpPercent}%.`
      : '';
    this.notice('wave', `Attack ${resolved.absoluteWave} started${earlyRelease ? ' early' : ''}.${cycleNotice}`);
    return true;
  }

  setPaused(paused: boolean): boolean {
    if (this.state.phase !== 'prewave' && this.state.phase !== 'active') return false;
    this.state.paused = paused;
    this.notice('notice', paused ? 'Simulation paused.' : 'Simulation resumed.');
    return true;
  }

  togglePaused(): boolean { return this.setPaused(!this.state.paused); }

  placeTower(definitionId: TowerId, x: number, y: number): boolean { return this.placeStructure(definitionId, x, y); }

  upgradeTower(towerId: number, upgradeId: TowerUpgradeId): boolean {
    if (this.state.paused || this.state.phase === 'victory' || this.state.phase === 'defeat') return false;
    const tower = this.state.towers.find((candidate) => candidate.id === towerId);
    const upgrade = TOWER_UPGRADES[upgradeId];
    if (!tower || !upgrade || upgrade.parentId !== tower.definitionId) {
      this.notice('notice', 'Upgrade rejected: this path does not belong to the selected tower.');
      return false;
    }
    if (tower.upgradeId === upgradeId) {
      this.notice('notice', `${upgrade.name} is already installed.`);
      return false;
    }
    if (!this.isTowerUpgradeAvailable(upgradeId)) {
      this.notice('notice', `Upgrade rejected: ${this.getTowerUpgradeRequirement(upgradeId)}`);
      return false;
    }
    const replacingUpgrade = tower.upgradeId !== undefined;
    const price = replacingUpgrade ? Math.ceil(upgrade.cost * 0.75) : upgrade.cost;
    if (this.state.cash < price) {
      this.notice('notice', `Upgrade rejected: ${upgrade.name} requires $${price}.`);
      return false;
    }
    const previousName = tower.upgradeId ? TOWER_UPGRADES[tower.upgradeId].name : undefined;
    const previousReload = resolveTowerDefinition(tower.definitionId, tower.upgradeId).reloadTicks.value;
    const nextReload = resolveTowerDefinition(tower.definitionId, upgradeId).reloadTicks.value;
    const reloadProgress = previousReload > 0 ? Math.min(1, tower.cooldownTicks / previousReload) : 0;
    this.state.cash -= price;
    this.state.towers = this.state.towers.map((candidate) => candidate.id === towerId
      ? {
        ...candidate,
        upgradeId,
        cost: candidate.cost + price,
        cooldownTicks: replacingUpgrade ? Math.ceil(nextReload * reloadProgress) : candidate.cooldownTicks,
        targetId: null,
        lockTargetId: null,
        lockTicks: 0,
      }
      : candidate);
    this.notice('build', replacingUpgrade
      ? `${previousName} replaced with ${upgrade.name} for $${price}.`
      : `${upgrade.name} installed for $${price}.`);
    return true;
  }

  upgradeBuilding(buildingId: number, upgradeId: BuildingUpgradeId): boolean {
    if (this.state.paused || this.state.phase === 'victory' || this.state.phase === 'defeat') return false;
    this.refreshBuildingUpgradeUnlocks();
    const building = this.state.buildings.find((candidate) => candidate.id === buildingId);
    const upgrade = BUILDING_UPGRADES[upgradeId];
    if (!building || !upgrade || upgrade.parentId !== building.definitionId) {
      this.notice('notice', 'Upgrade rejected: this path does not belong to the selected building.');
      return false;
    }
    if (building.upgradeId) {
      this.notice('notice', 'Upgrade rejected: this building has already committed to a path.');
      return false;
    }
    if (!this.isBuildingUpgradeUnlocked(upgradeId)) {
      this.notice('notice', `Upgrade rejected: ${upgrade.requirement}.`);
      return false;
    }
    if (this.state.cash < upgrade.cost) {
      this.notice('notice', `Upgrade rejected: ${upgrade.name} requires $${upgrade.cost}.`);
      return false;
    }
    const sourceAbility: AbilityId | undefined = upgradeId === 'wall.mine-factory'
      ? 'mine'
      : upgradeId === 'wall.wire-factory'
        ? 'barbed-wire'
        : undefined;
    const firstSource = sourceAbility !== undefined && this.abilitySourceCount(sourceAbility) === 0;
    this.state.cash -= upgrade.cost;
    this.state.buildings = this.state.buildings.map((candidate) => candidate.id === buildingId
      ? { ...candidate, upgradeId, cost: candidate.cost + upgrade.cost }
      : candidate);
    if (firstSource && sourceAbility) this.state.abilityCooldowns[sourceAbility] = 0;
    if (upgradeId === 'silo.thermonuclear') this.state.counters.thermonuclearBuilt += 1;
    this.refreshBuildingUpgradeUnlocks();
    this.notice('build', `${upgrade.name} installed for $${upgrade.cost}.`);
    return true;
  }

  getStructureAt(x: number, y: number): DeployedStructureSelection | null {
    const cell = this.toCell(x, y);
    const tower = this.state.towers.find((candidate) => this.occupiesCell(candidate, cell.x, cell.y));
    if (tower) return { kind: 'tower', id: tower.id, definitionId: tower.definitionId };
    const building = this.state.buildings.find((candidate) => this.occupiesBuildingCell(candidate, cell.x, cell.y));
    return building ? { kind: 'building', id: building.id, definitionId: building.definitionId } : null;
  }

  placeStructure(definitionId: CatalogueId, x: number, y: number): boolean {
    if (this.state.paused || this.state.phase === 'victory' || this.state.phase === 'defeat') return false;
    const definition = isTowerId(definitionId) ? TOWERS[definitionId] : BUILDINGS[definitionId];
    const cell = this.toCell(x, y);
    if (!definition) return false;
    if (!this.isUnlocked(definitionId)) {
      this.notice('notice', 'Placement rejected: construction chain is still locked.');
      return false;
    }
    if (!this.canPlaceStructure(definitionId, x, y)) {
      this.notice('notice', 'Placement rejected: the full footprint needs clear solid canyon ground.');
      return false;
    }
    if (this.state.cash < definition.cost.value) {
      this.notice('notice', 'Placement rejected: insufficient credits.');
      return false;
    }
    this.state.cash -= definition.cost.value;
    if (isTowerId(definitionId)) {
      const towerDefinition = TOWERS[definitionId];
      const tower: TowerState = {
        id: this.nextEntityId++, definitionId,
        position: { x: cell.x * CELL_SIZE + CELL_SIZE / 2, y: cell.y * CELL_SIZE + CELL_SIZE / 2 },
        cell, angle: -Math.PI / 2, cooldownTicks: towerDefinition.reloadTicks.value, targetId: null, cost: towerDefinition.cost.value,
      };
      this.state.towers = [...this.state.towers, tower];
      if (definitionId === 'cd1.tower.missile') this.state.counters.builtMissiles += 1;
      if (definitionId === 'cd1.tower.aa') this.state.counters.builtAA += 1;
      if (definitionId === 'cd1.tower.howitzer') this.state.counters.builtHowitzers += 1;
      if (definitionId === 'cd1.tower.gauss') this.state.counters.builtGauss += 1;
      if (definitionId === 'cd1.tower.oil') this.state.counters.builtOil += 1;
      if (definitionId === 'cd1.tower.nulificator') this.state.counters.builtNulificators += 1;
    } else {
      const sourceAbility: AbilityId | undefined = definitionId === 'cd1.building.silo'
        ? 'missile'
        : definitionId === 'cd1.building.wall-factory'
          ? 'wall'
          : definitionId === 'cd1.building.laboratory'
            ? 'stop-time'
            : undefined;
      const firstSource = sourceAbility !== undefined && this.abilitySourceCount(sourceAbility) === 0;
      const building: BuildingState = { id: this.nextEntityId++, definitionId, cell, cost: definition.cost.value };
      this.state.buildings = [...this.state.buildings, building];
      if (firstSource) this.state.abilityCooldowns[sourceAbility] = 0;
    }
    this.state.counters.builtTotal += 1;
    this.refreshUnlocks();
    this.refreshBuildingUpgradeUnlocks();
    this.notice('build', `${structureLabel(definitionId)} deployed for $${definition.cost.value}.`);
    return true;
  }

  sellAt(x: number, y: number): boolean {
    if (this.state.paused) return false;
    const cell = this.toCell(x, y);
    const tower = this.state.towers.find((candidate) => this.occupiesCell(candidate, cell.x, cell.y));
    const building = this.state.buildings.find((candidate) => this.occupiesBuildingCell(candidate, cell.x, cell.y));
    const wall = this.state.walls.find((candidate) => candidate.cell.x === cell.x && candidate.cell.y === cell.y);
    if (!tower && !building && !wall) {
      this.notice('notice', 'Select a deployed defense node to sell.');
      return false;
    }
    const cost = tower?.cost ?? building?.cost ?? wall?.cost ?? 0;
    const refund = Math.round(cost * SELL_FRACTION.value);
    this.state.cash += refund;
    // A tower or 2x2 building can be supported by a wall. Sell that top structure first;
    // the next click exposes and sells the underlying wall.
    if (tower) this.state.towers = this.state.towers.filter((candidate) => candidate.id !== tower.id);
    else if (building) this.state.buildings = this.state.buildings.filter((candidate) => candidate.id !== building.id);
    else if (wall) this.state.walls = this.state.walls.filter((candidate) => candidate.id !== wall.id);
    this.refreshUnlocks();
    this.notice('sell', `Defense recycled for $${refund}.`);
    return true;
  }

  useAbility(ability: AbilityId, x: number, y: number): boolean {
    if (this.state.paused || !this.abilityReady(ability)) {
      this.notice('notice', 'Ability is unavailable or recharging.');
      return false;
    }
    if (ability === 'wall') return this.placeWall(x, y);
    if (ability === 'mine') return this.placeMine(x, y);
    if (ability === 'barbed-wire') return this.placeBarbedWire(x, y);
    if (ability === 'missile') {
      const targetPosition = { x: Math.max(0, Math.min(LOGICAL_BOARD_SIZE, x)), y: Math.max(0, Math.min(LOGICAL_BOARD_SIZE, y)) };
      this.state.projectiles = [...this.state.projectiles, {
        id: this.nextEntityId++, ownerId: 0, targetId: 0, targetPosition,
        position: { ...ABILITY_RULES.missile.launch }, speed: ABILITY_RULES.missile.speedPerTick, damage: this.getMissileDamage(), color: 0xffe15b,
        angle: angleTo(ABILITY_RULES.missile.launch, targetPosition), turnRate: 0.1,
        piercesShield: true, kind: 'missile', splashRadius: ABILITY_RULES.missile.radius,
      }];
      this.state.missileActions += 1;
      this.visual('ability', ABILITY_RULES.missile.launch, {
        target: targetPosition, color: 0xffe15b, radius: ABILITY_RULES.missile.radius, ability: 'missile-cast', projectileKind: 'missile',
      });
    } else {
      const center = { x, y };
      this.state.enemies = this.state.enemies.map((enemy) => {
        if (distanceSquared(enemy.position, center) > ABILITY_RULES['stop-time'].radius ** 2) return enemy;
        this.visual('status', enemy.position, { sourceId: enemy.id, status: 'stopped', durationTicks: ABILITY_RULES['stop-time'].durationTicks, color: 0x77d8ff });
        return { ...enemy, stoppedTicks: Math.max(enemy.stoppedTicks, ABILITY_RULES['stop-time'].durationTicks) };
      });
      this.visual('ability', center, { color: 0x77d8ff, radius: ABILITY_RULES['stop-time'].radius, ability: 'stop-time' });
    }
    this.startAbilityCooldown(ability);
    this.refreshUnlocks();
    this.notice('notice', ability === 'missile' ? 'Silo missile launched.' : 'Stop Time field engaged for five seconds.');
    return true;
  }

  step(ticks = 1): void {
    for (let remaining = 0; remaining < ticks; remaining += 1) this.stepOnce();
  }

  private stepOnce(): void {
    if (this.state.paused || this.state.phase === 'victory' || this.state.phase === 'defeat') return;
    this.state.tick += 1;
    this.tickAbilityCooldowns();
    if (this.resolveWave(this.state.nextWaveIndex + 1) && this.state.nextWaveCountdownTicks > 0) this.state.nextWaveCountdownTicks -= 1;
    if (this.resolveWave(this.state.nextWaveIndex + 1) && this.state.nextWaveCountdownTicks <= 0) this.beginNextWave(false);
    if (this.state.phase === 'active') this.spawnDueEnemies();
    this.updateHazards();
    this.moveEnemies();
    this.updateTowers();
    this.updateProjectiles();
    this.removeDeadEnemies();
    this.finishWaveIfResolved();
  }

  private getScript(): readonly WaveDefinition[] { return WAVE_SCRIPTS[this.state.mapId][this.state.difficultyId]; }

  private resolveWave(absoluteWave: number) {
    return resolveWave(this.getScript(), this.state.difficultyId, this.state.runMode, absoluteWave);
  }

  private currentWave(): WaveDefinition | undefined {
    return this.state.activeWave ? this.resolveWave(this.state.activeWave.absoluteWave)?.definition : undefined;
  }

  private spawnDueEnemies(): void {
    const active = this.state.activeWave;
    const wave = this.currentWave();
    if (!active || !wave || active.spawned >= active.total || this.state.tick < active.nextSpawnTick) return;
    // The original program rows create their full unit group together. Retain a non-zero path for
    // any later authored encounter that explicitly requests staggered spawns.
    do {
      const group = wave.groups[active.groupIndex];
      this.spawnEnemy(group, active.groupSpawned);
      active.spawned += 1;
      active.groupSpawned += 1;
      active.nextSpawnTick = this.state.tick + wave.spawnEveryTicks.value;
      if (active.groupSpawned >= group.count) {
        active.groupIndex += 1;
        active.groupSpawned = 0;
      }
    } while (wave.spawnEveryTicks.value === 0 && active.spawned < active.total && active.groupIndex < wave.groups.length);
    if (active.groupIndex >= wave.groups.length) {
      active.groupIndex = wave.groups.length;
    }
  }

  private spawnEnemy(group: WaveGroup, waveIndex: number): void {
    const definition = enemyDefinition(group.domain, group.size, group.level);
    const map = MAPS[this.state.mapId];
    const path = group.domain === 'ground' ? this.createGroundPath(map, waveIndex) : this.createAirPath();
    const hpMultiplier = this.state.activeWave?.hpMultiplier ?? 1;
    const health = scaleEnemyHealth(definition.hp.value, hpMultiplier);
    const enemy: EnemyState = {
      id: this.nextEntityId++,
      definitionId: definition.id,
      domain: group.domain,
      size: group.size,
      level: group.level,
      hp: health,
      maxHp: health,
      reward: definition.reward.value,
      ...(this.state.runMode === 'infinite' ? { scoreMultiplier: hpMultiplier } : {}),
      speed: definition.speed.value * (group.effect === 'fast' ? 1.5 : 1),
      throttle: definition.throttlePerTick.value,
      turnRate: definition.turnPerTick.value,
      velocity: definition.speed.value * (group.effect === 'fast' ? 1.5 : 1),
      heading: angleTo(path[0], path[1] ?? path[0]),
      effect: group.effect,
      slowTicks: 0,
      confuseTicks: 0,
      stoppedTicks: 0,
      path,
      pathIndex: 1,
      position: cloneVec(path[0]),
      progress: 0,
    };
    this.state.enemies = [...this.state.enemies, enemy];
  }

  private updateHazards(): void {
    this.wireSlowThisTick = new Map();
    const groundEnemies = this.state.enemies
      .filter((enemy) => enemy.domain === 'ground' && enemy.hp > 0)
      .sort((left, right) => left.id - right.id);

    const remainingMines: MineState[] = [];
    this.state.mines.forEach((mine) => {
      if (mine.fuseTicks !== undefined) {
        const fuseTicks = mine.fuseTicks - 1;
        if (fuseTicks <= 0) {
          const center = this.cellCenter(mine.cell.x, mine.cell.y);
          this.visual('ability', center, {
            ability: 'mine-impact',
            projectileKind: 'mine',
            radius: ABILITY_RULES.mine.radius,
            color: 0xffa43a,
          });
          groundEnemies.forEach((enemy) => {
            if (distanceSquared(enemy.position, center) > ABILITY_RULES.mine.radius ** 2) return;
            const hpBefore = enemy.hp;
            const shieldMultiplier = enemy.effect === 'shield' ? 0.1 : 1;
            enemy.hp -= ABILITY_RULES.mine.damage * shieldMultiplier;
            if (hpBefore > 0 && enemy.hp <= 0) this.state.counters.aoeKills += 1;
          });
          return;
        }
        remainingMines.push({ ...mine, fuseTicks });
        return;
      }
      const triggered = groundEnemies.some((enemy) => this.enemyOccupiesCell(enemy, mine.cell));
      remainingMines.push(triggered ? { ...mine, fuseTicks: ABILITY_RULES.mine.fuseTicks } : mine);
    });
    this.state.mines = remainingMines;

    const remainingWires: BarbedWireState[] = [];
    this.state.barbedWires.forEach((wire) => {
      const occupants = groundEnemies.filter((enemy) => this.enemyOccupiesCell(enemy, wire.cell));
      const previousOccupants = new Set(wire.insideEnemyIds);
      let usesRemaining = wire.usesRemaining;
      let exhausted = false;
      const retainedOccupants: number[] = [];
      for (const enemy of occupants) {
        const slowPercent = ABILITY_RULES['barbed-wire'].slowBySize[enemy.size];
        this.wireSlowThisTick.set(enemy.id, slowPercent);
        if (slowPercent > 0) this.recordDistinctQuestEnemy('slowedEnemyIds', enemy.id);
        retainedOccupants.push(enemy.id);
        if (previousOccupants.has(enemy.id)) continue;
        usesRemaining -= ABILITY_RULES['barbed-wire'].useCostBySize[enemy.size];
        if (usesRemaining <= 0) {
          exhausted = true;
          break;
        }
      }
      if (!exhausted) {
        remainingWires.push({ ...wire, usesRemaining, insideEnemyIds: retainedOccupants });
      }
    });
    this.state.barbedWires = remainingWires;
    this.refreshBuildingUpgradeUnlocks();
  }

  private enemyOccupiesCell(enemy: EnemyState, cell: { x: number; y: number }): boolean {
    return Math.floor(enemy.position.x / CELL_SIZE) === cell.x
      && Math.floor(enemy.position.y / CELL_SIZE) === cell.y;
  }

  private moveEnemies(): void {
    const remaining: EnemyState[] = [];
    for (const enemy of this.state.enemies) {
      const corrosionStacks = (enemy.corrosionStacks ?? []).filter((stack) => stack.ticks > 0);
      const burningStacks = (enemy.burningStacks ?? []).filter((stack) => stack.ticks > 0);
      const statusProcessed = {
        ...enemy,
        hp: enemy.hp
          - corrosionStacks.reduce((damage, stack) => damage + stack.damagePerSecond * TICK_SECONDS, 0)
          - burningStacks.reduce((damage, stack) => damage + stack.damagePerSecond * TICK_SECONDS, 0),
        corrosionStacks: corrosionStacks
          .map((stack) => ({ ...stack, ticks: stack.ticks - 1 }))
          .filter((stack) => stack.ticks > 0),
        burningStacks: burningStacks
          .map((stack) => ({ ...stack, ticks: stack.ticks - 1 }))
          .filter((stack) => stack.ticks > 0),
      };
      let current = statusProcessed.effect === 'autorepair'
        ? { ...statusProcessed, hp: Math.min(statusProcessed.maxHp, statusProcessed.hp + statusProcessed.maxHp * 0.005) }
        : statusProcessed.invertedEffect === 'autorepair'
          ? { ...statusProcessed, hp: statusProcessed.hp - statusProcessed.maxHp * 0.005 * (statusProcessed.invertedMagnitude ?? 0.5) }
          : statusProcessed;
      const stopped = current.stoppedTicks > 0;
      const slowed = current.slowTicks > 0;
      const confused = current.confuseTicks > 0;
      const statusSpeedMultiplier = slowed ? 1 - (current.slowPercent ?? 0.25) : 1;
      const wireSpeedMultiplier = 1 - (this.wireSlowThisTick.get(current.id) ?? 0);
      const speedMultiplier = Math.min(statusSpeedMultiplier, wireSpeedMultiplier);
      let travelBudget = stopped ? 0 : current.speed * speedMultiplier * TICK_SECONDS;
      let hops = 0;
      while (current.pathIndex < current.path.length && hops < 32) {
        const target = current.path[current.pathIndex];
        const distance = Math.sqrt(distanceSquared(current.position, target));
        if (distance <= 0.0001) {
          current = { ...current, position: cloneVec(target), pathIndex: current.pathIndex + 1 };
          hops += 1;
          continue;
        }
        const desiredHeading = angleTo(current.position, target);
        const confuseTurnMultiplier = 1 - (current.confusePercent ?? DEFAULT_CONFUSE_PERCENT);
        const heading = approachAngle(current.heading, desiredHeading, current.turnRate * (confused ? confuseTurnMultiplier : 1));
        const alignment = confused ? Math.max(0, Math.cos(wrapAngle(desiredHeading - heading))) : 1;
        const baseVelocity = stopped ? 0 : current.speed * speedMultiplier;
        const velocity = baseVelocity * alignment;
        if (travelBudget <= 0) {
          current = { ...current, heading, velocity };
          break;
        }
        // Position is constrained to the prevalidated ordered route segment. Heading still turns at its
        // recovered rate, but it cannot steer a ground unit diagonally through a canyon corner.
        const availableTravel = travelBudget * alignment;
        const travel = Math.min(availableTravel, distance);
        const position = moveTowards(current.position, target, travel);
        if (current.domain === 'ground' && !this.canGroundTraverse(current.position, position)) {
          // A wall can only race a stale segment in injected/replayed state: regenerate the exit-field route
          // immediately and keep this tick's travel budget. Normal movement never waits on a failed steer.
          const path = this.createGroundPath(MAPS[this.state.mapId], 0, current.position);
          if (path.length > 1) {
            current = { ...current, heading, velocity, path, pathIndex: 1 };
            continue;
          }
          // Preserve the position only if it is already isolated; this cannot arise from a legal wall placement.
          current = { ...current, heading, velocity: 0 };
          break;
        }
        current = { ...current, heading, velocity, position, progress: current.progress + travel };
        travelBudget = Math.max(0, availableTravel - travel);
        if (distance <= travel + 0.0001) {
          current = { ...current, position: cloneVec(target), pathIndex: current.pathIndex + 1 };
          hops += 1;
          continue;
        }
        break;
      }
      // Durations take effect for the full movement frame in which their value is positive, then tick down.
      current.slowTicks = Math.max(0, current.slowTicks - 1);
      if (current.slowTicks === 0) current.slowPercent = undefined;
      current.confuseTicks = Math.max(0, current.confuseTicks - 1);
      if (current.confuseTicks === 0) current.confusePercent = undefined;
      current.stoppedTicks = Math.max(0, current.stoppedTicks - 1);
      if (current.pathIndex >= current.path.length) {
        this.state.lives -= LEAK_LIFE_DAMAGE.value;
        this.notice('leak', 'An enemy escaped the canyon. -1 life');
      } else {
        remaining.push(current);
      }
    }
    this.state.enemies = remaining;
    if (this.state.lives <= 0) {
      this.state.lives = 0;
      this.state.phase = 'defeat';
      this.state.activeWave = null;
      this.notice('result', 'Run lost. The canyon was overrun.');
    }
  }

  /** The path is allowed to run along a road edge, but never through rock or a blocking wall. */
  private canGroundTraverse(from: Vec2, to: Vec2): boolean {
    const steps = Math.max(1, Math.ceil(Math.hypot(to.x - from.x, to.y - from.y)));
    for (let step = 1; step <= steps; step += 1) {
      const fraction = step / steps;
      const x = from.x + (to.x - from.x) * fraction;
      const y = from.y + (to.y - from.y) * fraction;
      if (!this.isOpenGroundPoint(x, y)) return false;
    }
    return true;
  }

  private isOpenGroundPoint(x: number, y: number): boolean {
    if (x < 0 || x > LOGICAL_BOARD_SIZE || y < 0 || y > LOGICAL_BOARD_SIZE) return true;
    const axisCells = (coordinate: number): readonly number[] => {
      const lower = Math.floor(coordinate / CELL_SIZE);
      const onBoundary = Math.abs(coordinate - Math.round(coordinate / CELL_SIZE) * CELL_SIZE) <= 0.0001;
      return onBoundary ? [lower - 1, lower] : [lower];
    };
    return axisCells(x).some((cellX) => axisCells(y).some((cellY) =>
      cellX >= 0 && cellX < GRID_SIZE && cellY >= 0 && cellY < GRID_SIZE
      && MAPS[this.state.mapId].roadCells.includes(cellKey(cellX, cellY)) && !this.hasWall(cellX, cellY)));
  }

  private updateTowers(): void {
    const nextTowers: TowerState[] = [];
    for (const tower of this.state.towers) {
      const definition = resolveTowerDefinition(tower.definitionId, tower.upgradeId);
      const target = this.acquireTarget(tower);
      const next: TowerState = { ...tower, cooldownTicks: Math.max(0, tower.cooldownTicks - 1), targetId: target?.id ?? null };
      if (!target || next.cooldownTicks > 0) {
        next.lockTargetId = null;
        next.lockTicks = 0;
      } else if (definition.targetLockTicks !== undefined) {
        if (next.lockTargetId !== target.id) {
          next.lockTargetId = target.id;
          next.lockTicks = 1;
        } else {
          next.lockTicks = Math.min(definition.targetLockTicks, (next.lockTicks ?? 0) + 1);
        }
      }
      if (target) {
        const targetAngle = angleTo(next.position, target.position);
        const alignedBeforeTurn = Math.abs(wrapAngle(targetAngle - next.angle)) < TOWER_FIRE_ALIGNMENT_RADIANS.value;
        next.angle = definition.turnRate.value === 0
          ? targetAngle
          : approachAngle(next.angle, targetAngle, definition.turnRate.value * TICK_SECONDS);
        const aligned = definition.turnRate.value === 0
          ? alignedBeforeTurn
          : Math.abs(wrapAngle(targetAngle - next.angle)) < TOWER_FIRE_ALIGNMENT_RADIANS.value;
        const targetLockComplete = definition.targetLockTicks === undefined
          || (next.lockTargetId === target.id && (next.lockTicks ?? 0) >= definition.targetLockTicks);
        if (aligned && next.cooldownTicks === 0 && targetLockComplete) {
          if (next.definitionId === 'cd1.tower.tesla') this.state.counters.teslaShots += 1;
          if (next.upgradeId === 'gauss.hammer') this.state.counters.hammerShots += 1;
          const color = this.projectileColor(definition.projectile);
          const isElectricPulse = definition.projectile === 'electricField';
          const isOrbitalStrike = definition.orbitalStrike !== undefined;
          const projectileKind = isOrbitalStrike ? 'orbitalStrike' : definition.projectile;
          this.state.projectiles = [...this.state.projectiles, {
            id: this.nextEntityId++, ownerId: next.id, targetId: isElectricPulse ? 0 : target.id,
            position: isOrbitalStrike ? cloneVec(target.position) : cloneVec(next.position),
            speed: isElectricPulse || isOrbitalStrike ? 0 : definition.projectileSpeed.value || Math.sqrt(distanceSquared(next.position, target.position)) * FIXED_TICK_HZ,
            angle: angleTo(next.position, target.position),
            turnRate: definition.projectileTurnRate.value,
            damage: definition.damage.value,
            color,
            piercesShield: definition.piercesShield === true,
            kind: projectileKind,
            splashRadius: isElectricPulse ? this.getEffectiveTowerRange(next.definitionId, next.upgradeId) : definition.splashRadius,
            targetDomains: definition.domains,
            ...(definition.retargetsOnDestroyed ? { retargetsOnDestroyed: true } : {}),
            ...(definition.shieldDamageMultiplier !== undefined ? { shieldDamageMultiplier: definition.shieldDamageMultiplier } : {}),
            ...(definition.unshieldedDamageMultiplier !== undefined ? { unshieldedDamageMultiplier: definition.unshieldedDamageMultiplier } : {}),
            ...(definition.stun ? { stun: { ...definition.stun } } : {}),
            ...(definition.splitDamage ? { splitDamage: true } : {}),
            ...(definition.invertsEffects ? { invertsEffects: { ...definition.invertsEffects } } : {}),
            ...(definition.corrosion ? { corrosion: { ...definition.corrosion } } : {}),
            ...(definition.chainBounceHits !== undefined ? { chainBounceHits: definition.chainBounceHits } : {}),
            ...(definition.splitShot ? { splitShot: { ...definition.splitShot } } : {}),
            ...(definition.orbitalStrike ? { orbitalStrike: { ...definition.orbitalStrike } } : {}),
            ...(definition.burning ? { burning: { ...definition.burning } } : {}),
            ...(isElectricPulse ? { targetPosition: cloneVec(next.position) }
              : definition.projectile === 'ball' || definition.projectile === 'mortarShell'
                ? { targetPosition: cloneVec(target.position) }
                : {}),
            ...(definition.slow ? { slow: { percent: definition.slow.percent, ticks: definition.slow.ticks } } : {}),
            ...(definition.confuse ? { confuse: { percent: definition.confuse.percent, ticks: definition.confuse.ticks } } : {}),
            ...(definition.clearsEffects ? { clearsEffects: true } : {}),
            ...(definition.chainTargets ? { chainTargets: definition.chainTargets } : {}),
            ...(definition.chainRadius !== undefined ? { chainRadius: definition.chainRadius } : {}),
          }];
          this.visual('fire', isOrbitalStrike ? { x: target.position.x, y: target.position.y - 120 } : next.position, {
            sourceId: next.id,
            target: target.position,
            projectileKind,
            color,
          });
          next.cooldownTicks = definition.reloadTicks.value;
          next.lockTargetId = null;
          next.lockTicks = 0;
          this.refreshBuildingUpgradeUnlocks();
        }
      }
      nextTowers.push(next);
    }
    this.state.towers = nextTowers;
  }

  private acquireTarget(tower: TowerState): EnemyState | undefined {
    const definition = resolveTowerDefinition(tower.definitionId, tower.upgradeId);
    const domains = definition.domains;
    const range = this.getEffectiveTowerRange(tower.definitionId, tower.upgradeId);
    const rangeSquared = range * range;
    const eligible = (enemy: EnemyState): boolean => enemy.hp > 0
      && domains.includes(enemy.domain)
      && distanceSquared(tower.position, enemy.position) <= rangeSquared;
    const locked = tower.targetId === null ? undefined : this.state.enemies.find((enemy) => enemy.id === tower.targetId);
    if (locked && eligible(locked)) return locked;
    return this.state.enemies
      .filter(eligible)
      .sort((a, b) => distanceSquared(a.position, tower.position) - distanceSquared(b.position, tower.position) || a.id - b.id)[0];
  }

  private updateProjectiles(): void {
    const nextProjectiles: ProjectileState[] = [];
    const enemyById = new Map(this.state.enemies.map((enemy) => [enemy.id, enemy]));
    for (const projectile of this.state.projectiles) {
      let nextProjectile = projectile;
      let target = enemyById.get(projectile.targetId);
      if (target && target.hp <= 0) target = undefined;
      if (!target && projectile.retargetsOnDestroyed) {
        const domains = projectile.targetDomains ?? [];
        target = this.state.enemies
          .filter((enemy) => enemy.hp > 0 && domains.includes(enemy.domain))
          .sort((left, right) =>
            distanceSquared(left.position, projectile.position) - distanceSquared(right.position, projectile.position)
            || left.id - right.id)[0];
        if (target) nextProjectile = { ...projectile, targetId: target.id };
      }
      const fixedPointShell = nextProjectile.kind === 'ball' || nextProjectile.kind === 'mortarShell';
      const targetPosition = fixedPointShell ? nextProjectile.targetPosition : target?.position ?? nextProjectile.targetPosition;
      if (!targetPosition) continue;
      const distance = Math.sqrt(distanceSquared(nextProjectile.position, targetPosition));
      const travel = nextProjectile.speed * TICK_SECONDS;
      if (distance <= travel + PROJECTILE_HIT_RADIUS.value) {
        this.applyProjectileImpact(nextProjectile, targetPosition, fixedPointShell ? undefined : target);
      } else {
        const desiredAngle = angleTo(nextProjectile.position, targetPosition);
        const angle = approachAngle(nextProjectile.angle, desiredAngle, nextProjectile.turnRate);
        nextProjectiles.push({ ...nextProjectile, angle, position: { x: nextProjectile.position.x + Math.cos(angle) * travel, y: nextProjectile.position.y + Math.sin(angle) * travel } });
      }
    }
    this.state.projectiles = nextProjectiles;
  }

  private applyProjectileImpact(projectile: ProjectileState, position: Vec2, directTarget?: EnemyState): void {
    this.visual('impact', position, {
      projectileKind: projectile.kind,
      color: projectile.color,
      radius: projectile.splashRadius,
      ...(projectile.kind === 'missile' ? { ability: 'missile-impact' as const } : {}),
    });
    const candidates = projectile.splashRadius > 0
      ? this.state.enemies.filter((enemy) =>
        (projectile.targetDomains === undefined || projectile.targetDomains.includes(enemy.domain))
        && distanceSquared(enemy.position, position) <= projectile.splashRadius * projectile.splashRadius)
      : directTarget ? [directTarget] : [];
    const chain: EnemyState[] = [];
    if (projectile.chainTargets && directTarget) {
      const used = new Set([directTarget.id]);
      let previous = directTarget;
      const radiusSquared = (projectile.chainRadius ?? Number.POSITIVE_INFINITY) ** 2;
      for (let hop = 1; hop < projectile.chainTargets; hop += 1) {
        const next = this.state.enemies
          .filter((enemy) =>
            (projectile.targetDomains ?? [directTarget.domain]).includes(enemy.domain)
            && !used.has(enemy.id)
            && enemy.hp > 0
            && distanceSquared(enemy.position, previous.position) <= radiusSquared)
          .sort((left, right) =>
            distanceSquared(left.position, previous.position) - distanceSquared(right.position, previous.position)
            || left.id - right.id)[0];
        if (!next) break;
        chain.push(next);
        used.add(next.id);
        previous = next;
      }
    }
    const affected = [...new Map([...candidates, ...chain].map((enemy) => [enemy.id, enemy])).values()];
    const chainPool = directTarget
      ? [...new Map([directTarget, ...chain].map((enemy) => [enemy.id, enemy])).values()]
      : [];
    const hitSequence = projectile.chainBounceHits && chainPool.length >= 2
      ? Array.from({ length: projectile.chainBounceHits }, (_, index) => chainPool[index % chainPool.length])
      : affected;
    const countsAsAoe = projectile.kind === 'missile'
      || projectile.kind === 'electricField'
      || (projectile.splashRadius > 0 && !projectile.chainTargets && !projectile.splitShot);
    for (let index = 0; index < hitSequence.length; index += 1) {
      const enemy = hitSequence[index];
      if (enemy.hp <= 0) break;
      if (index > 0 && projectile.chainTargets) {
        const previous = hitSequence[index - 1];
        this.visual('fire', previous.position, {
          sourceId: projectile.ownerId,
          target: enemy.position,
          projectileKind: projectile.kind,
          color: projectile.color,
        });
      }
      const shielded = enemy.effect === 'shield';
      const splitDamage = projectile.splitDamage && affected.length > 0 ? projectile.damage / affected.length : projectile.damage;
      const shieldRuleDamage = shielded && !projectile.piercesShield ? splitDamage * 0.1 : splitDamage;
      const targetMultiplier = shielded
        ? projectile.shieldDamageMultiplier ?? 1
        : projectile.unshieldedDamageMultiplier ?? 1;
      const invertedShieldMultiplier = enemy.invertedEffect === 'shield'
        ? 1 + 0.9 * (enemy.invertedMagnitude ?? 0.5)
        : 1;
      const damage = shieldRuleDamage * targetMultiplier * invertedShieldMultiplier;
      const hpBefore = enemy.hp;
      enemy.hp -= damage;
      if (countsAsAoe && hpBefore > 0 && enemy.hp <= 0) this.state.counters.aoeKills += 1;
      if (projectile.kind === 'missile' && enemy.hp > 0) {
        this.recordDistinctQuestEnemy('missileSurvivorIds', enemy.id);
      }
      if (projectile.slow) {
        enemy.slowTicks = Math.max(enemy.slowTicks, projectile.slow.ticks);
        enemy.slowPercent = Math.max(enemy.slowPercent ?? 0, projectile.slow.percent);
        this.recordDistinctQuestEnemy('slowedEnemyIds', enemy.id);
        this.visual('status', enemy.position, { sourceId: enemy.id, status: 'slow', durationTicks: projectile.slow.ticks, color: 0x68dc76, projectileKind: projectile.kind });
      }
      if (projectile.confuse) {
        enemy.confuseTicks = Math.max(enemy.confuseTicks, projectile.confuse.ticks);
        enemy.confusePercent = projectile.confuse.percent;
        this.visual('status', enemy.position, { sourceId: enemy.id, status: 'confuse', durationTicks: projectile.confuse.ticks, color: 0xe7a14b, projectileKind: projectile.kind });
      }
      if (projectile.stun && this.randomInt(10_000) < Math.round(projectile.stun.chance * 10_000)) {
        enemy.stoppedTicks = Math.max(enemy.stoppedTicks, projectile.stun.ticks);
        this.visual('status', enemy.position, { sourceId: enemy.id, status: 'stopped', durationTicks: projectile.stun.ticks, color: 0x78ddff, projectileKind: projectile.kind });
      }
      if (projectile.corrosion && !shielded) {
        enemy.corrosionStacks = [
          ...(enemy.corrosionStacks ?? []),
          { ticks: projectile.corrosion.ticks, damagePerSecond: projectile.corrosion.damagePerSecond },
        ];
        this.visual('status', enemy.position, { sourceId: enemy.id, status: 'corrosion', durationTicks: projectile.corrosion.ticks, color: 0xc7e83f, projectileKind: projectile.kind });
      }
      if (projectile.burning && !shielded) {
        enemy.burningStacks = [
          ...(enemy.burningStacks ?? []),
          { ticks: projectile.burning.ticks, damagePerSecond: projectile.burning.damagePerSecond },
        ];
        this.visual('status', enemy.position, { sourceId: enemy.id, status: 'burning', durationTicks: projectile.burning.ticks, color: 0xff7b30, projectileKind: projectile.kind });
      }
      if (projectile.invertsEffects && enemy.effect) {
        const invertedEffect = enemy.effect;
        const magnitude = projectile.invertsEffects.magnitude;
        if (invertedEffect === 'fast') {
          enemy.speed *= (1 - 0.5 * magnitude) / 1.5;
          enemy.velocity = Math.min(enemy.velocity, enemy.speed);
        }
        enemy.effect = undefined;
        enemy.invertedEffect = invertedEffect;
        enemy.invertedMagnitude = magnitude;
        this.visual('status', enemy.position, { sourceId: enemy.id, status: 'invert', color: 0xf07cff, projectileKind: projectile.kind });
      }
      if (projectile.clearsEffects) {
        enemy.effect = undefined;
        if (enemy.invertedEffect === 'fast') {
          enemy.speed = enemyDefinition(enemy.domain, enemy.size, enemy.level).speed.value;
          enemy.velocity = Math.min(enemy.velocity, enemy.speed);
        }
        enemy.invertedEffect = undefined;
        enemy.invertedMagnitude = undefined;
        enemy.slowTicks = 0;
        enemy.slowPercent = undefined;
        enemy.confuseTicks = 0;
        enemy.confusePercent = undefined;
        enemy.corrosionStacks = [];
        enemy.burningStacks = [];
        this.visual('status', enemy.position, { sourceId: enemy.id, status: 'clear', color: 0xe3a4ff, projectileKind: projectile.kind });
      }
    }
    if (projectile.splitShot && directTarget) {
      const splitTargets = this.state.enemies
        .filter((enemy) =>
          enemy.id !== directTarget.id
          && enemy.domain === 'air'
          && enemy.hp > 0
          && distanceSquared(enemy.position, directTarget.position) <= projectile.splitShot!.radius ** 2)
        .sort((left, right) =>
          distanceSquared(left.position, directTarget.position) - distanceSquared(right.position, directTarget.position)
          || left.id - right.id)
        .slice(0, projectile.splitShot.targets);
      splitTargets.forEach((enemy) => {
        const shielded = enemy.effect === 'shield';
        const shieldRuleDamage = shielded && !projectile.piercesShield
          ? projectile.splitShot!.damage * 0.1
          : projectile.splitShot!.damage;
        const invertedShieldMultiplier = enemy.invertedEffect === 'shield'
          ? 1 + 0.9 * (enemy.invertedMagnitude ?? 0.5)
          : 1;
        enemy.hp -= shieldRuleDamage * invertedShieldMultiplier;
        this.visual('fire', directTarget.position, {
          sourceId: projectile.ownerId,
          target: enemy.position,
          projectileKind: 'gaussRail',
          color: projectile.color,
        });
        this.visual('impact', enemy.position, { projectileKind: 'gaussRail', color: projectile.color });
      });
    }
    if (projectile.orbitalStrike) {
      this.visual('impact', position, {
        projectileKind: 'orbitalGround',
        color: 0xff9b58,
        radius: projectile.orbitalStrike.groundSplashRadius,
      });
      this.state.enemies
        .filter((enemy) =>
          enemy.domain === 'ground'
          && enemy.hp > 0
          && distanceSquared(enemy.position, position) <= projectile.orbitalStrike!.groundSplashRadius ** 2)
        .forEach((enemy) => {
          const shielded = enemy.effect === 'shield';
          const shieldRuleDamage = shielded
            ? projectile.orbitalStrike!.groundDamage * 0.1
            : projectile.orbitalStrike!.groundDamage;
          const invertedShieldMultiplier = enemy.invertedEffect === 'shield'
            ? 1 + 0.9 * (enemy.invertedMagnitude ?? 0.5)
            : 1;
          const hpBefore = enemy.hp;
          enemy.hp -= shieldRuleDamage * invertedShieldMultiplier;
          if (hpBefore > 0 && enemy.hp <= 0) this.state.counters.aoeKills += 1;
        });
    }
    this.refreshBuildingUpgradeUnlocks();
  }

  private removeDeadEnemies(): void {
    const remaining: EnemyState[] = [];
    for (const enemy of this.state.enemies) {
      if (enemy.hp > 0) {
        remaining.push(enemy);
        continue;
      }
      const reward = enemy.reward * (1 + this.countBuildings('cd1.building.recycler') * 0.5);
      this.state.cash += reward;
      this.state.score += Math.round(reward * SCORE_PER_REWARD.value * (enemy.scoreMultiplier ?? 1));
      this.state.counters.kills += 1;
      this.state.counters.earned += reward;
      if (enemy.domain === 'air') this.state.counters.destroyedAir += 1;
      this.refreshUnlocks();
      this.notice('kill', `Hostile neutralized. +$${reward}`);
    }
    this.state.enemies = remaining;
  }

  private finishWaveIfResolved(): void {
    const active = this.state.activeWave;
    if (!active || active.spawned < active.total || this.state.enemies.length > 0 || this.state.projectiles.length > 0) return;
    const finished = this.resolveWave(active.absoluteWave);
    if (!finished) return;
    this.state.activeWave = null;
    this.state.completedWaves = Math.max(this.state.completedWaves, active.absoluteWave);
    if (this.state.runMode === 'fixed' && !this.resolveWave(this.state.nextWaveIndex + 1)) {
      this.state.phase = 'victory';
      this.state.score = Math.round((this.state.counters.earned * SCORE_PER_REWARD.value + this.state.lives * 10_000) * DIFFICULTIES[this.state.difficultyId].scoreMultiplier.value);
      this.notice('result', `Canyon secured. ${finished.absoluteWave} attacks survived.`);
    } else {
      this.state.phase = 'prewave';
      const next = this.resolveWave(this.state.nextWaveIndex + 1);
      const transition = next && next.baseWave === 1 && next.completedCycles > 0
        ? ` Cycle ${next.cycle} ready: enemy HP ${next.hpPercent}%.`
        : ' Ready for the next attack.';
      this.notice('wave', `Attack ${finished.absoluteWave} resolved.${transition}`);
    }
  }

  /** A top structure may be placed on canyon rock, an existing wall, or a mixed 2x2 support footprint. */
  canPlaceStructure(definitionId: CatalogueId, x: number, y: number): boolean {
    const anchor = this.toCell(x, y);
    const [width, height] = STRUCTURE_FOOTPRINT_CELLS[definitionId];
    for (let offsetY = 0; offsetY < height; offsetY += 1) {
      for (let offsetX = 0; offsetX < width; offsetX += 1) {
        const cellX = anchor.x + offsetX;
        const cellY = anchor.y + offsetY;
        if ((!this.isBuildableCell(cellX, cellY) && !this.hasWall(cellX, cellY)) || this.isStructureOccupied(cellX, cellY)) return false;
      }
    }
    return true;
  }

  private occupiesCell(tower: TowerState, x: number, y: number): boolean {
    const [width, height] = STRUCTURE_FOOTPRINT_CELLS[tower.definitionId];
    return x >= tower.cell.x && x < tower.cell.x + width && y >= tower.cell.y && y < tower.cell.y + height;
  }

  private occupiesBuildingCell(building: BuildingState, x: number, y: number): boolean {
    const [width, height] = STRUCTURE_FOOTPRINT_CELLS[building.definitionId];
    return x >= building.cell.x && x < building.cell.x + width && y >= building.cell.y && y < building.cell.y + height;
  }

  private isStructureOccupied(x: number, y: number): boolean {
    return this.state.towers.some((tower) => this.occupiesCell(tower, x, y))
      || this.state.buildings.some((building) => this.occupiesBuildingCell(building, x, y));
  }

  private hasWall(x: number, y: number): boolean { return this.state.walls.some((wall) => wall.cell.x === x && wall.cell.y === y); }

  private countTowers(definitionId?: TowerId): number {
    return definitionId ? this.state.towers.filter((tower) => tower.definitionId === definitionId).length : this.state.towers.length;
  }

  private countBuildings(definitionId?: BuildingId): number {
    return definitionId ? this.state.buildings.filter((building) => building.definitionId === definitionId).length : this.state.buildings.length;
  }

  private countBuildingUpgrades(upgradeId: BuildingUpgradeId): number {
    return this.state.buildings.filter((building) => building.upgradeId === upgradeId).length;
  }

  private refreshUnlocks(): void {
    const unlocks = this.state.unlocked;
    // Unlocking a predecessor can make an already-satisfied successor active in the same deterministic refresh.
    for (let pass = 0; pass < CATALOGUE.length; pass += 1) {
      let changed = false;
      this.getUnlockProgress().forEach((progress) => {
        if (progress.active && progress.current >= progress.target && !unlocks[progress.id]) {
          unlocks[progress.id] = true;
          changed = true;
        }
      });
      if (!changed) return;
    }
  }

  private refreshBuildingUpgradeUnlocks(): void {
    this.getBuildingUpgradeProgress().forEach((progress) => {
      if (progress.current >= progress.target) {
        this.state.buildingUpgradeUnlocked[progress.id as BuildingUpgradeId] = true;
      }
    });
  }

  private recordDistinctQuestEnemy(
    fact: 'missileSurvivorIds' | 'slowedEnemyIds',
    enemyId: number,
  ): void {
    const current = this.state.upgradeQuestFacts[fact];
    if (current.includes(enemyId)) return;
    this.state.upgradeQuestFacts = {
      ...this.state.upgradeQuestFacts,
      [fact]: [...current, enemyId],
    };
    this.refreshBuildingUpgradeUnlocks();
  }

  private abilitySourceCount(ability: AbilityId): number {
    if (ability === 'missile') return this.countBuildings('cd1.building.silo');
    if (ability === 'wall') {
      return this.state.buildings.filter((building) =>
        building.definitionId === 'cd1.building.wall-factory' && building.upgradeId === undefined).length;
    }
    if (ability === 'mine') return this.countBuildingUpgrades('wall.mine-factory');
    if (ability === 'barbed-wire') return this.countBuildingUpgrades('wall.wire-factory');
    return this.countBuildings('cd1.building.laboratory');
  }

  private startAbilityCooldown(ability: AbilityId): void {
    this.state.abilityCooldowns[ability] = ACTIVE_COOLDOWN_TICKS;
  }

  private tickAbilityCooldowns(): void {
    // Original CAbility.tick: each matching source contributes one timer tick.
    abilityIds.forEach((ability) => {
      if (this.state.abilityCooldowns[ability] === undefined) return;
      this.state.abilityCooldowns[ability] = Math.max(0, this.state.abilityCooldowns[ability] - this.abilitySourceCount(ability));
    });
  }

  private creditSkippedCooldowns(skippedTicks: number): void {
    if (skippedTicks <= 0) return;
    // Early release must grant the same recharge that waiting through these ticks would.
    abilityIds.forEach((ability) => {
      if (this.state.abilityCooldowns[ability] === undefined) return;
      this.state.abilityCooldowns[ability] = Math.max(
        0,
        this.state.abilityCooldowns[ability] - skippedTicks * this.abilitySourceCount(ability),
      );
    });
  }

  private placeWall(x: number, y: number): boolean {
    const cell = this.toCell(x, y);
    const map = MAPS[this.state.mapId];
    if (!this.canPlaceWall(x, y)) {
      const isRoute = cell.x > 0 && cell.x < GRID_SIZE - 1 && cell.y > 0 && cell.y < GRID_SIZE - 1 && map.roadCells.includes(cellKey(cell.x, cell.y));
      this.notice('notice', isRoute ? 'Wall placement is blocked by an occupant or would seal the canyon.' : 'Wall placement needs an open interior route cell.');
      return false;
    }
    const candidate: WallState = { id: this.nextEntityId++, cell, cost: 0 };
    this.state.walls = [...this.state.walls, candidate];
    this.state.counters.wallsBuilt += 1;
    this.rerouteGroundEnemies();
    this.startAbilityCooldown('wall');
    this.refreshUnlocks();
    this.refreshBuildingUpgradeUnlocks();
    this.notice('build', 'Wall deployed; route remains open.');
    return true;
  }

  /** Shared wall action hit-test for both the simulation and its ability preview. */
  canPlaceWall(x: number, y: number): boolean {
    const cell = this.toCell(x, y);
    const map = MAPS[this.state.mapId];
    if (cell.x <= 0 || cell.x >= GRID_SIZE - 1 || cell.y <= 0 || cell.y >= GRID_SIZE - 1) return false;
    if (!map.roadCells.includes(cellKey(cell.x, cell.y)) || this.hasWall(cell.x, cell.y) || this.isStructureOccupied(cell.x, cell.y)) return false;
    if (this.state.enemies.some((enemy) => Math.floor(enemy.position.x / CELL_SIZE) === cell.x && Math.floor(enemy.position.y / CELL_SIZE) === cell.y)) return false;
    return this.hasReachableGroundRoute([...this.state.walls, { id: -1, cell, cost: 0 }]);
  }

  canPlaceMine(x: number, y: number): boolean { return this.canPlacePathHazard(x, y); }

  canPlaceBarbedWire(x: number, y: number): boolean { return this.canPlacePathHazard(x, y); }

  private canPlacePathHazard(x: number, y: number): boolean {
    const cell = this.toCell(x, y);
    if (!MAPS[this.state.mapId].roadCells.includes(cellKey(cell.x, cell.y))) return false;
    if (this.hasWall(cell.x, cell.y) || this.isStructureOccupied(cell.x, cell.y)) return false;
    if (this.state.mines.some((mine) => mine.cell.x === cell.x && mine.cell.y === cell.y)) return false;
    if (this.state.barbedWires.some((wire) => wire.cell.x === cell.x && wire.cell.y === cell.y)) return false;
    return !this.state.enemies.some((enemy) =>
      Math.floor(enemy.position.x / CELL_SIZE) === cell.x
      && Math.floor(enemy.position.y / CELL_SIZE) === cell.y);
  }

  private placeMine(x: number, y: number): boolean {
    if (!this.canPlaceMine(x, y)) {
      this.notice('notice', 'Mine placement needs an otherwise-empty path tile.');
      return false;
    }
    const cell = this.toCell(x, y);
    const mine: MineState = { id: this.nextEntityId++, cell };
    this.state.mines = [...this.state.mines, mine];
    this.startAbilityCooldown('mine');
    this.visual('ability', this.cellCenter(cell.x, cell.y), { ability: 'mine-placed', color: 0xd6a942 });
    this.notice('build', 'Mine armed on the canyon path.');
    return true;
  }

  private placeBarbedWire(x: number, y: number): boolean {
    if (!this.canPlaceBarbedWire(x, y)) {
      this.notice('notice', 'Barbed Wire placement needs an otherwise-empty path tile.');
      return false;
    }
    const cell = this.toCell(x, y);
    const wire: BarbedWireState = {
      id: this.nextEntityId++,
      cell,
      usesRemaining: ABILITY_RULES['barbed-wire'].uses,
      insideEnemyIds: [],
    };
    this.state.barbedWires = [...this.state.barbedWires, wire];
    this.startAbilityCooldown('barbed-wire');
    this.visual('ability', this.cellCenter(cell.x, cell.y), { ability: 'barbed-wire-placed', color: 0xb7a37b });
    this.notice('build', 'Barbed Wire deployed on the canyon path.');
    return true;
  }

  private hasReachableGroundRoute(walls: readonly WallState[]): boolean {
    const map = MAPS[this.state.mapId];
    const wallCells = new Set(walls.map((wall) => cellKey(wall.cell.x, wall.cell.y)));
    const distance = this.groundExitDistance(map, walls);
    for (let y = 0; y < GRID_SIZE; y += 1) {
      for (let x = 0; x < GRID_SIZE; x += 1) {
        if (map.roadCells.includes(cellKey(x, y)) && !wallCells.has(cellKey(x, y)) && distance[y][x] < 0) return false;
      }
    }
    return true;
  }

  private projectileColor(kind: string): number {
    return ({ rocket: 0xffb15a, cruiseMissile: 0xffd45e, ball: 0xe9ce72, mortarShell: 0xf09b4a, electricField: 0x75d7ff, laserBeam: 0xf2505a, nulifyBall: 0xe3a4ff, aaShell: 0x9ee9ff, slowShot: 0x75c886, chainBlast: 0xf8ec78, gaussRail: 0x83e5ff, confuseBall: 0xd98948 } as Record<string, number>)[kind] ?? 0xffe15b;
  }

  private isBuildableCell(x: number, y: number): boolean {
    if (x < 0 || x >= GRID_SIZE || y < 0 || y >= GRID_SIZE) return false;
    return MAPS[this.state.mapId].surfaceRows[y]?.[x] === 'T';
  }

  private toCell(x: number, y: number): { x: number; y: number } {
    return { x: Math.floor(x / CELL_SIZE), y: Math.floor(y / CELL_SIZE) };
  }

  /** Original CMap route model: edge candidates, exit-seeded flood field, then ordered orthogonal descent. */
  private createGroundPath(map: MapDefinition, waveIndex: number, fromPosition?: Vec2, pendingEntry?: Vec2): readonly Vec2[] {
    const blocked = new Set(this.state.walls.map((wall) => cellKey(wall.cell.x, wall.cell.y)));
    const road = (x: number, y: number): boolean => x >= 0 && x < GRID_SIZE && y >= 0 && y < GRID_SIZE && map.roadCells.includes(cellKey(x, y)) && !blocked.has(cellKey(x, y));
    const entries: { x: number; y: number }[] = [];
    for (let index = 0; index < GRID_SIZE; index += 1) {
      if (road(index, 0)) entries.push({ x: index, y: 0 });
      if (road(0, index)) entries.push({ x: 0, y: index });
    }
    const distance = this.groundExitDistance(map, this.state.walls);
    const reachableEntries = entries.filter((cell) => distance[cell.y][cell.x] >= 0);
    const currentCell = fromPosition ? this.toCell(fromPosition.x, fromPosition.y) : undefined;
    const beforeEntry = Boolean(fromPosition && (fromPosition.x < 0 || fromPosition.y < 0));
    const retainedEntry = beforeEntry && pendingEntry
      ? reachableEntries.find((cell) => {
        const boundary = this.entryBoundary(cell);
        return boundary.x === pendingEntry.x && boundary.y === pendingEntry.y;
      })
      : undefined;
    const start = fromPosition && !beforeEntry
      ? currentCell && road(currentCell.x, currentCell.y) && distance[currentCell.y][currentCell.x] >= 0 ? currentCell : undefined
      : retainedEntry ?? reachableEntries[this.randomInt(reachableEntries.length)];
    if (!start) return fromPosition ? [cloneVec(fromPosition)] : [{ x: -CELL_SIZE / 2, y: -CELL_SIZE / 2 }];
    const path: Vec2[] = fromPosition
      ? [cloneVec(fromPosition)]
      : [start.y === 0
        ? { x: this.cellCenter(start.x, start.y).x + this.randomInt(16) - 8, y: -CELL_SIZE / 2 - 35 * waveIndex }
        : { x: -CELL_SIZE / 2 - 35 * waveIndex, y: this.cellCenter(start.x, start.y).y + this.randomInt(16) - 8 }];
    if (!fromPosition || beforeEntry) path.push(this.entryBoundary(start));
    const routeCells = [start];
    let cursor = start;
    while (distance[cursor.y][cursor.x] > 0) {
      const nextCells = this.orthogonalNeighbors(cursor.x, cursor.y).filter((candidate) => distance[candidate.y]?.[candidate.x] === distance[cursor.y][cursor.x] - 1);
      const next = nextCells[0];
      if (!next) break;
      routeCells.push(next);
      cursor = next;
    }
    for (let index = 0; index < routeCells.length; index += 1) {
      const cell = routeCells[index];
      const next = routeCells[index + 1];
      const outgoing = next ? this.sharedBoundary(cell, next) : this.exitBoundary(cell);
      const incomingDirection = index > 0
        ? { x: cell.x - routeCells[index - 1].x, y: cell.y - routeCells[index - 1].y }
        : (!fromPosition || beforeEntry) ? (cell.y === 0 ? { x: 0, y: 1 } : { x: 1, y: 0 }) : undefined;
      const outgoingDirection = next
        ? { x: next.x - cell.x, y: next.y - cell.y }
        : cell.y === GRID_SIZE - 1 ? { x: 0, y: 1 } : { x: 1, y: 0 };
      if (incomingDirection && incomingDirection.x * outgoingDirection.x + incomingDirection.y * outgoingDirection.y === 0) {
        this.appendTurnCurve(path, outgoing, this.cellCenter(cell.x, cell.y));
      } else {
        path.push(outgoing);
      }
    }
    path.push(cursor.y === GRID_SIZE - 1
      ? { x: this.cellCenter(cursor.x, cursor.y).x, y: LOGICAL_BOARD_SIZE + CELL_SIZE / 2 }
      : { x: LOGICAL_BOARD_SIZE + CELL_SIZE / 2, y: this.cellCenter(cursor.x, cursor.y).y });
    return path;
  }

  /** Replace a sharp boundary-to-boundary corner with a short in-cell quadratic turn. */
  private appendTurnCurve(path: Vec2[], outgoing: Vec2, control: Vec2): void {
    const incoming = path[path.length - 1];
    for (let sample = 1; sample <= TURN_CURVE_SAMPLES; sample += 1) {
      const t = sample / TURN_CURVE_SAMPLES;
      const inverse = 1 - t;
      path.push({
        x: inverse * inverse * incoming.x + 2 * inverse * t * control.x + t * t * outgoing.x,
        y: inverse * inverse * incoming.y + 2 * inverse * t * control.y + t * t * outgoing.y,
      });
    }
  }

  private groundExitDistance(map: MapDefinition, walls: readonly WallState[]): number[][] {
    const wallCells = new Set(walls.map((wall) => cellKey(wall.cell.x, wall.cell.y)));
    const road = (x: number, y: number): boolean => x >= 0 && x < GRID_SIZE && y >= 0 && y < GRID_SIZE && map.roadCells.includes(cellKey(x, y)) && !wallCells.has(cellKey(x, y));
    const distance = Array.from({ length: GRID_SIZE }, () => Array<number>(GRID_SIZE).fill(-1));
    const queue: { x: number; y: number }[] = [];
    for (let index = 0; index < GRID_SIZE; index += 1) {
      if (road(index, GRID_SIZE - 1)) queue.push({ x: index, y: GRID_SIZE - 1 });
      if (road(GRID_SIZE - 1, index)) queue.push({ x: GRID_SIZE - 1, y: index });
    }
    queue.forEach((cell) => { distance[cell.y][cell.x] = 0; });
    for (let head = 0; head < queue.length; head += 1) {
      const current = queue[head];
      for (const neighbor of this.orthogonalNeighbors(current.x, current.y)) {
        if (!road(neighbor.x, neighbor.y) || distance[neighbor.y][neighbor.x] !== -1) continue;
        distance[neighbor.y][neighbor.x] = distance[current.y][current.x] + 1;
        queue.push(neighbor);
      }
    }
    return distance;
  }

  private entryBoundary(cell: { x: number; y: number }): Vec2 {
    return cell.y === 0 ? { x: this.cellCenter(cell.x, cell.y).x, y: 0 } : { x: 0, y: this.cellCenter(cell.x, cell.y).y };
  }

  private exitBoundary(cell: { x: number; y: number }): Vec2 {
    return cell.y === GRID_SIZE - 1 ? { x: this.cellCenter(cell.x, cell.y).x, y: LOGICAL_BOARD_SIZE } : { x: LOGICAL_BOARD_SIZE, y: this.cellCenter(cell.x, cell.y).y };
  }

  private sharedBoundary(a: { x: number; y: number }, b: { x: number; y: number }): Vec2 {
    return { x: (a.x + b.x + 1) * CELL_SIZE / 2, y: (a.y + b.y + 1) * CELL_SIZE / 2 };
  }

  private rerouteGroundEnemies(): void {
    const map = MAPS[this.state.mapId];
    this.state.enemies = this.state.enemies.map((enemy) => {
      if (enemy.domain !== 'ground' || enemy.pathIndex >= enemy.path.length) return enemy;
      if (enemy.position.x > LOGICAL_BOARD_SIZE || enemy.position.y > LOGICAL_BOARD_SIZE) return enemy;
      const path = this.createGroundPath(map, 0, enemy.position, enemy.path[enemy.pathIndex]);
      return { ...enemy, path, pathIndex: Math.min(1, path.length) };
    });
  }

  private createAirPath(): readonly Vec2[] {
    const column = 4 + this.randomInt(6);
    const x = column * CELL_SIZE + CELL_SIZE / 2;
    return [{ x, y: -CELL_SIZE / 2 }, { x, y: LOGICAL_BOARD_SIZE + CELL_SIZE / 2 }];
  }

  private orthogonalNeighbors(x: number, y: number): readonly { x: number; y: number }[] {
    return [{ x, y: y - 1 }, { x: x - 1, y }, { x: x + 1, y }, { x, y: y + 1 }]
      .filter((cell) => cell.x >= 0 && cell.x < GRID_SIZE && cell.y >= 0 && cell.y < GRID_SIZE);
  }

  private cellCenter(x: number, y: number): Vec2 { return { x: x * CELL_SIZE + CELL_SIZE / 2, y: y * CELL_SIZE + CELL_SIZE / 2 }; }

  private randomInt(maximumExclusive: number): number {
    if (maximumExclusive <= 1) return 0;
    this.randomState = (Math.imul(this.randomState, 1664525) + 1013904223) >>> 0;
    return this.randomState % maximumExclusive;
  }

  private visual(kind: VisualEvent['kind'], position: Vec2, details: Omit<VisualEvent, 'id' | 'tick' | 'kind' | 'position'> = {}): void {
    const event: VisualEvent = {
      id: this.nextVisualEventId++,
      tick: this.state.tick,
      kind,
      position: cloneVec(position),
      ...details,
      ...(details.target ? { target: cloneVec(details.target) } : {}),
    };
    this.state.visualEvents = [...this.state.visualEvents.slice(-(MAX_VISUAL_EVENTS - 1)), event];
  }

  private notice(type: SimEvent['type'], message: string): void {
    const event: SimEvent = { tick: this.state.tick, type, message };
    this.state.events = [...this.state.events.slice(-(MAX_EVENTS - 1)), event];
    this.state.lastNotice = message;
  }
}
